// Odontograma (033) — geometría compartida entre la pantalla (SVG) y el PDF
// firmado (pdf-lib): superficies de cada pieza y símbolos oficiales de la
// simbología del bloque K de la ficha SNS-MSP/HCU-form.033/2021 (instructivo,
// bloque H, pág. 164 del PDF fuente). Todo en coordenadas unitarias con Y hacia
// abajo (como SVG); quien dibuja escala y traslada. Sin I/O ni dependencias.
import type { CodigoHallazgoOdontograma } from './odontograma';

/** V vestibular, L lingual/palatina, M mesial, D distal, O oclusal/incisal (centro). */
export const SUPERFICIES_DENTALES = ['V', 'L', 'M', 'D', 'O'] as const;
export type SuperficieDental = (typeof SUPERFICIES_DENTALES)[number];

/** Estado de una superficie: caries (rojo, patología actual) u obturación (azul, tratamiento realizado). */
export type EstadoSuperficie = 'caries' | 'obturado';
export type SuperficiesPieza = Readonly<Partial<Record<SuperficieDental, EstadoSuperficie>>>;

export function esSuperficieDental(v: unknown): v is SuperficieDental {
  return typeof v === 'string' && (SUPERFICIES_DENTALES as readonly string[]).includes(v);
}

export function esEstadoSuperficie(v: unknown): v is EstadoSuperficie {
  return v === 'caries' || v === 'obturado';
}

export function esTemporal(pieza: string): boolean {
  return /^[5-8]\d$/.test(pieza);
}

/** Lado del dibujo donde cae cada superficie ('centro' = oclusal/incisal). */
export type LadoDibujo = 'arriba' | 'abajo' | 'izquierda' | 'derecha' | 'centro';

/**
 * Posición anatómica de las superficies según el cuadrante FDI (primer dígito):
 * en la arcada superior el vestibular queda arriba y el palatino abajo; en la
 * inferior, al revés (el vestibular siempre mira hacia afuera de la boca).
 * Mesial = hacia la línea media: a la derecha del dibujo en los cuadrantes 1/4/5/8
 * (lado izquierdo de la ficha) y a la izquierda en 2/3/6/7.
 */
export function ladoDeSuperficie(pieza: string, superficie: SuperficieDental): LadoDibujo {
  const cuadrante = Number(pieza[0]);
  const superior = cuadrante === 1 || cuadrante === 2 || cuadrante === 5 || cuadrante === 6;
  const ladoIzquierdoFicha = cuadrante === 1 || cuadrante === 4 || cuadrante === 5 || cuadrante === 8;
  switch (superficie) {
    case 'O':
      return 'centro';
    case 'V':
      return superior ? 'arriba' : 'abajo';
    case 'L':
      return superior ? 'abajo' : 'arriba';
    case 'M':
      return ladoIzquierdoFicha ? 'derecha' : 'izquierda';
    case 'D':
      return ladoIzquierdoFicha ? 'izquierda' : 'derecha';
  }
}

export type Punto = readonly [number, number];

const K_INTERNO = 0.4;
const PASOS_ARCO = 8;

function arco(radio: number, desde: number, hasta: number): Punto[] {
  const puntos: Punto[] = [];
  for (let i = 0; i <= PASOS_ARCO; i++) {
    const a = ((desde + ((hasta - desde) * i) / PASOS_ARCO) * Math.PI) / 180;
    puntos.push([radio * Math.cos(a), radio * Math.sin(a)]);
  }
  return puntos;
}

/**
 * Polígono (en coordenadas unitarias, radio/semilado 1, Y hacia abajo) de un lado de la pieza:
 * pieza permanente = cuadrado con cuadrado interior; temporal = círculo con círculo interior.
 */
export function poligonoDeLado(lado: LadoDibujo, temporal: boolean): Punto[] {
  const k = K_INTERNO;
  if (!temporal) {
    switch (lado) {
      case 'arriba':
        return [[-1, -1], [1, -1], [k, -k], [-k, -k]];
      case 'abajo':
        return [[-1, 1], [1, 1], [k, k], [-k, k]];
      case 'izquierda':
        return [[-1, -1], [-k, -k], [-k, k], [-1, 1]];
      case 'derecha':
        return [[1, -1], [k, -k], [k, k], [1, 1]];
      case 'centro':
        return [[-k, -k], [k, -k], [k, k], [-k, k]];
    }
  }
  // Ángulos con Y hacia abajo: arriba = -90°.
  const tramos: Record<Exclude<LadoDibujo, 'centro'>, [number, number]> = { arriba: [-135, -45], derecha: [-45, 45], abajo: [45, 135], izquierda: [135, 225] };
  if (lado === 'centro') return arco(k, 0, 360).slice(0, -1);
  const [desde, hasta] = tramos[lado];
  return [...arco(1, desde, hasta), ...arco(k, hasta, desde)];
}

/** Polígono de una superficie concreta de una pieza concreta. */
export function poligonoDeSuperficie(pieza: string, superficie: SuperficieDental): Punto[] {
  return poligonoDeLado(ladoDeSuperficie(pieza, superficie), esTemporal(pieza));
}

/** Contorno exterior de la pieza (cuadrado o círculo). */
export function contornoDePieza(temporal: boolean): Punto[] {
  return temporal ? arco(1, 0, 360).slice(0, -1) : [[-1, -1], [1, -1], [1, 1], [-1, 1]];
}

// ------------------------------------------------------------------ símbolos

export type Trazo =
  | { readonly t: 'linea'; readonly a: Punto; readonly b: Punto; readonly punteada?: boolean }
  | { readonly t: 'poli'; readonly pts: readonly Punto[] }
  | { readonly t: 'circulo'; readonly r: number }
  | { readonly t: 'texto'; readonly s: string };

export type SimboloOdontograma = 'x' | 'x-circulo' | 'triangulo' | 'corona' | 'protesis-total' | 'protesis-fija' | 'protesis-removible' | 'asterisco' | 'ausente';

const TRAZOS: Record<SimboloOdontograma, readonly Trazo[]> = {
  x: [
    { t: 'linea', a: [-1, -1], b: [1, 1] },
    { t: 'linea', a: [-1, 1], b: [1, -1] },
  ],
  'x-circulo': [
    { t: 'linea', a: [-0.6, -0.6], b: [0.6, 0.6] },
    { t: 'linea', a: [-0.6, 0.6], b: [0.6, -0.6] },
    { t: 'circulo', r: 0.85 },
  ],
  triangulo: [{ t: 'poli', pts: [[0, -0.9], [0.9, 0.75], [-0.9, 0.75]] }],
  corona: [
    { t: 'poli', pts: [[-0.85, -0.85], [0.85, -0.85], [0.85, 0.85], [-0.85, 0.85]] },
    { t: 'poli', pts: [[-0.4, -0.4], [0.4, -0.4], [0.4, 0.4], [-0.4, 0.4]] },
  ],
  'protesis-total': [
    { t: 'linea', a: [-1, -0.25], b: [1, -0.25] },
    { t: 'linea', a: [-1, 0.25], b: [1, 0.25] },
  ],
  'protesis-fija': [
    { t: 'poli', pts: [[-0.3, -0.3], [0.3, -0.3], [0.3, 0.3], [-0.3, 0.3]] },
    { t: 'linea', a: [-1, 0], b: [-0.3, 0], punteada: true },
    { t: 'linea', a: [0.3, 0], b: [1, 0], punteada: true },
  ],
  'protesis-removible': [{ t: 'linea', a: [-1, 0], b: [1, 0], punteada: true }],
  asterisco: [
    { t: 'linea', a: [-1, 0], b: [1, 0] },
    { t: 'linea', a: [-0.5, -0.87], b: [0.5, 0.87] },
    { t: 'linea', a: [-0.5, 0.87], b: [0.5, -0.87] },
  ],
  ausente: [{ t: 'texto', s: 'A' }],
};

export function trazosDeSimbolo(simbolo: SimboloOdontograma): readonly Trazo[] {
  return TRAZOS[simbolo];
}

/**
 * Símbolo oficial de cada hallazgo de pieza completa (`undefined` = sin símbolo propio:
 * sano, y caries/obturado, que se pintan por superficie). El asterisco del sellante va
 * "bajo el diente" (ver `simboloBajoPieza`).
 */
const SIMBOLO_POR_HALLAZGO: Partial<Record<CodigoHallazgoOdontograma, SimboloOdontograma>> = {
  'extraccion-indicada': 'x',
  'perdida-caries': 'x',
  'perdida-otra-causa': 'x-circulo',
  ausente: 'ausente',
  'endodoncia-por-realizar': 'triangulo',
  'endodoncia-realizada': 'triangulo',
  'corona-indicada': 'corona',
  'corona-realizada': 'corona',
  'protesis-total-indicada': 'protesis-total',
  'protesis-total-realizada': 'protesis-total',
  'protesis-fija-indicada': 'protesis-fija',
  'protesis-fija-realizada': 'protesis-fija',
  'protesis-removible-indicada': 'protesis-removible',
  'protesis-removible-realizada': 'protesis-removible',
  'sellante-necesario': 'asterisco',
  'sellante-realizado': 'asterisco',
};

export function simboloOficialDeHallazgo(hallazgo: string | undefined): SimboloOdontograma | undefined {
  return hallazgo ? SIMBOLO_POR_HALLAZGO[hallazgo as CodigoHallazgoOdontograma] : undefined;
}

/** El sellante se marca con un asterisco bajo el diente, no encima. */
export function simboloBajoPieza(simbolo: SimboloOdontograma): boolean {
  return simbolo === 'asterisco';
}

/** Hallazgos de pieza completa que pintan todas las superficies (datos anteriores a la captura por superficie). */
export function hallazgoPintaTodaLaPieza(hallazgo: string | undefined): EstadoSuperficie | undefined {
  return hallazgo === 'caries' ? 'caries' : hallazgo === 'obturado' ? 'obturado' : undefined;
}
